import { supabase } from '../../core/config/supabase';
import { TournamentEntity } from '../entities';

export class TournamentDataSource {
  async create(tournament: Omit<TournamentEntity, 'id' | 'created_at' | 'updated_at'>): Promise<TournamentEntity> {
    const { data, error } = await supabase
      .from('tournaments')
      .insert(tournament)
      .select()
      .single();

    if (error) throw new Error(error.message);
    if (!data) throw new Error('No data returned from insert');

    return data as TournamentEntity;
  }

  async getById(id: string): Promise<TournamentEntity | null> {
    const { data, error } = await supabase
      .from('tournaments')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // Not found
      throw new Error(error.message);
    }

    return data as TournamentEntity;
  }

  async getAll(limit: number = 20, offset: number = 0): Promise<TournamentEntity[]> {
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;

    if (!user) {
      throw new Error('User not authenticated');
    }

    // Usar la función RPC para obtener torneos propios + donde es admin
    const { data, error } = await supabase.rpc('get_user_tournaments', {
      requesting_user_id: user.id
    });

    if (error) throw new Error(error.message);

    // Apply pagination manually since RPC doesn't support .range()
    const tournaments = (data as TournamentEntity[]) || [];
    return tournaments.slice(offset, offset + limit);
  }

  async getPublic(limit: number = 20, offset: number = 0): Promise<TournamentEntity[]> {
    const { data, error } = await supabase
      .from('tournaments')
      .select('*')
      .eq('is_public', true)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) throw new Error(error.message);

    return (data as TournamentEntity[]) || [];
  }

  async update(id: string, tournament: Partial<TournamentEntity>): Promise<TournamentEntity> {
    const { data, error } = await supabase
      .from('tournaments')
      .update({ ...tournament, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();

    if (error) throw new Error(error.message);
    if (!data) throw new Error('No data returned from update');

    return data as TournamentEntity;
  }

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('tournaments')
      .delete()
      .eq('id', id);

    if (error) throw new Error(error.message);
  }
}
